import { IconButton } from '@mui/material';
import KeyboardArrowLeftOutlinedIcon from "@mui/icons-material/KeyboardArrowLeftOutlined";
import KeyboardArrowRightOutlinedIcon from "@mui/icons-material/KeyboardArrowRightOutlined";
import { IGalleryItemDetails, ArrowDirection } from 'modules/gallery';
import { useImageArrow } from '../hooks/useImageArrow';
import * as React from 'react';
import uuid from "uuid";

import './authoring/styles/inlineGallery.sass';

//This component is designed to be inserted as straight html to places like the tinymce editors - then later get replaced with itself on view to restore full functionality.
//Be careful if you add any new css stylings, as the MarkdownAsHtmlHelper in the backend may strip things unless explicitly allowed!
const InlineGallery: React.FunctionComponent<InlineGalleryProps> = props => {
    const { selectedImageDetails, showInteractables } = props;
    const [focusedImage, setFocusedImage] = React.useState(selectedImageDetails[0]);

    const selectAndScrollToImage = (imageToSet: IGalleryItemDetails, currentIndex: number) => {
        setFocusedImage(imageToSet);
        scrollImage(`inline-image-${uniqueGalleryId}-${currentIndex}`);
    }

    const onImageArrowClicked = useImageArrow({selectedImageDetails, focusedImage, selectAndScrollToImage});

    const onImageClicked = (ospreyId: string) => {
        const indexToSet = selectedImageDetails.findIndex(s => s.ospreyId === ospreyId);

        setFocusedImage(selectedImageDetails[indexToSet]);
        scrollImage(`inline-image-${uniqueGalleryId}-${indexToSet}`);
    };

    const scrollImage = (elementId: string) => {
        const elementToScroll = document.getElementById(elementId);
        const verticalElement = document.getElementById(props.verticalParentId ?? "");

        if(!!elementToScroll && !verticalElement) {
            elementToScroll.scrollIntoView({ block: 'nearest', inline: 'start' });
        }
        else if(!!elementToScroll && !!verticalElement) {
            const currentY = verticalElement.scrollTop;
            
            elementToScroll.scrollIntoView({ block: 'nearest', inline: 'start' });
            verticalElement.scrollTop = currentY;
        }
    }

    const getArrowIcon = (icon: React.JSX.Element, arrowDirection: ArrowDirection) => {
        return (
            <IconButton 
                onClick={() => onImageArrowClicked(arrowDirection)} 
                size="small" 
                edge="end" 
                className={`arrow-button-gallery ${arrowDirection}`}
            > 
                {icon}
            </IconButton>
        );
    }

    React.useEffect(() => {
        setFocusedImage(selectedImageDetails[0])
    }, [selectedImageDetails]);

    const previewImage = showInteractables ? focusedImage : selectedImageDetails[0];
    const uniqueGalleryId = uuid();

    return <div id={`inline-gallery-${uniqueGalleryId}`} className="mceNonEditable" contentEditable="false" suppressContentEditableWarning={true}>
        <div style={{display: "flex", justifyContent: "center", position: "relative"}}>
            <img style={{height: "300px", maxWidth: "100%", objectFit: "contain", margin: "0"}} src={previewImage.fileUrl ?? ""} alt={"Primary preview."}/>

            {showInteractables &&
                <React.Fragment>
                    {getArrowIcon(<KeyboardArrowLeftOutlinedIcon className="arrow-icon"/>, ArrowDirection.Left)}
                    {getArrowIcon(<KeyboardArrowRightOutlinedIcon className="arrow-icon"/>, ArrowDirection.Right)}
                </React.Fragment>
            }
        </div> 

        {previewImage.caption !== "" && 
            <div style={{fontSize: "14px", color: "#333333", margin: "16px 30px", textAlign: "center"}}>
                {previewImage.caption}
            </div>
        }

        <div id="inline-container" style={{display: "flex", flexDirection: "row", marginTop: "10px", overflowX: "auto", justifyContent: "center"}}>
            <div id="inline-wrapper" style={{display: "flex", overflowX: "auto", paddingBottom: "4px"}}>
                {selectedImageDetails.map((s, index) => {
                    const isFirst = index === 0;
                    const isLast = index === selectedImageDetails.length - 1;

                    return (
                        <img 
                            id={`inline-image-${uniqueGalleryId}-${index}`} 
                            data-osprey-id={s.ospreyId} 
                            data-image-caption={s.caption}
                            style={{height: "100px", margin: isFirst ? "0 5px 0 0" : isLast ? "0 0 0 5px" : "0px 5px", cursor: showInteractables ? "pointer" : ""}} 
                            key={index} 
                            src={s.fileUrl}
                            onClick={() => onImageClicked(s.ospreyId)}
                            alt={`Gallery preview item in list at index ${index + 1}`} //Index+1 because the average person doesn't index from 0
                        />
                    );
                })}
            </div>
        </div>
    </div>
};

interface InlineGalleryProps {
    selectedImageDetails: IGalleryItemDetails[];
    showInteractables: boolean; //If we're using things like MUI buttons, they'll be extremely broken when converted to html.
    verticalParentId?: string; //Used to prevent vertical scrolling.
};

export default(InlineGallery);